import React from 'react';
import { ICON_GREY } from './svgHelper';
import './payment-controls.css';

/**
 * Shared design tokens and controls for the Payment Platform tab headers
 * (Weekly / Company / Fleet / Driver Overview). These four filter rows are the
 * same Figma spec, so the values live here once instead of being re-transcribed
 * per tab — which is where the 41-vs-44px, `#d1cfd4`-vs-`#D1CFD4`, and
 * white-vs-dimmed disabled-text drift crept in.
 */

// Header control tokens (Figma).
export const PP_PURPLE = '#745D96'; // primary / enabled button fill (Eta-Purple)
export const PP_DISABLED_BG = '#C4C7CC'; // disabled fill (Eta-Disabled). Text stays white — the
                                         // design has no dimmed-disabled-text token.
export const PP_REFRESH_GREY = '#d1cfd4'; // refresh icon-button fill
export const UBUNTU_FONT = "'Ubuntu', system-ui, sans-serif";
export const PP_CONTROL_H = 41; // control height/width (px)
export { ICON_GREY };

/**
 * Square icon button used for the header's Search and Refresh actions (41×41, 5px
 * radius). When `disabled` it greys to PP_DISABLED_BG, shows a not-allowed cursor,
 * and forces its symbol to white so a disabled icon button matches its neighbours
 * (the disabled Search icon and the white text of the disabled action buttons) —
 * the single disabled contract the four tabs previously each did their own way.
 * The icon must accept a `color` prop (svgHelper's SearchIcon/RefreshIcon do).
 *
 * @param {Object} props
 * @param {JSX.Element} props.icon - The icon element to render (coloured/sized by the caller when enabled).
 * @param {Function} props.onClick
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.fill] - Enabled background; defaults to PP_PURPLE (Search). Refresh passes PP_REFRESH_GREY.
 * @param {string} [props.title] - Title + aria-label.
 * @param {string} [props.testId] - data-testid.
 * @returns {JSX.Element}
 */
export const IconButton = ({ icon, onClick, disabled = false, fill = PP_PURPLE, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testId}
    style={{
      height: PP_CONTROL_H,
      width: PP_CONTROL_H,
      borderRadius: 5,
      backgroundColor: disabled ? PP_DISABLED_BG : fill,
    }}
    className={`pp-icon-button flex items-center justify-center ${disabled ? 'cursor-not-allowed' : ''}`}
  >
    {disabled ? React.cloneElement(icon, { color: '#FFFFFF' }) : icon}
  </button>
);

/**
 * Purple pill action button (Download CSV / Upload / Statements). 41px tall, 5px
 * radius, Ubuntu 16px. Disabled greys the fill (Eta-Disabled) but keeps white text
 * (the design specifies no dimmed-disabled-text colour) and shows a not-allowed cursor.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button label (and optional leading icon).
 * @param {Function} props.onClick
 * @param {boolean} [props.disabled=false]
 * @returns {JSX.Element}
 */
export const ActionButton = ({ children, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      height: PP_CONTROL_H,
      borderRadius: 5,
      paddingLeft: 16,
      paddingRight: 16,
      gap: 10,
      fontFamily: UBUNTU_FONT,
      fontWeight: 400,
      fontSize: 16,
      backgroundColor: disabled ? PP_DISABLED_BG : PP_PURPLE,
      color: '#FFFFFF',
    }}
    className={`pp-action-button flex items-center whitespace-nowrap ${disabled ? 'cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);
